<template>
  <BaseModal
    small
    :heading="$t('website_service.onboarding.terms.heading')"
    useForm
    @submit="acceptTerms"
    @close="emit('close')"
  >
    <Flex
      direction="column"
      :gap="1.5"
    >
      <SubscriptionSummary :newTerms="!isLegacyTerms" />
      <ProcessSteps :newTerms="!isLegacyTerms" />

      <BaseText
        v-if="!viewOnly"
        v-test="'website-terms-learn-more'"
      >
        {{ $t('website_service.onboarding.terms.learn_more_before') }}
        <BaseText
          inline
          :href="helpCenterArticleUrl(13617949)"
        >
          {{ $t('website_service.onboarding.terms.learn_more_link_label') }}
        </BaseText>
        {{ $t('website_service.onboarding.terms.learn_more_after') }}
      </BaseText>

      <BaseCheckbox
        v-if="!viewOnly"
        :label="$t(isLegacyTerms
          ? 'website_service.onboarding.terms.confirmation'
          : 'website_service.onboarding.terms.confirmation_v2', { price, ...brandName() })"
        v-model="acceptedTerms"
        :disabled="isTrial"
        required
        v-test="'website-terms-checkbox'"
      />

      <BaseAlert
        v-if="isTrial"
        color="warning"
        :text="$t('website_service.onboarding.terms.trial_info')"
        :primaryAction="{
          text: $t('global.upgrade_now'),
          routerLink: { name: 'subscription' }
        }"
        v-test="'website-terms-trial-info'"
      />
    </Flex>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="emit('close')"
        v-test="'website-terms-close-button'"
      >
        {{ $t('global.actions.close') }}
      </BaseButton>
      <BaseButton
        v-if="!viewOnly"
        submitForm
        :loading="loading"
        :disabled="isTrial"
        v-test="'website-terms-submit-button'"
      >
        {{ isLegacyTerms ? $t('global.actions.submit') : $t('website_service.onboarding.terms.accept') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, inject, type Ref, ref } from 'vue';
import { brandName } from '@/helpers/brand-name';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { useCompanyStore } from '@/stores/company';
import type { Website } from '@/types';
import { ACCEPT_WEBSITE_TERMS } from '../../graphql';
import SubscriptionSummary from '../SubscriptionSummary.vue';
import ProcessSteps from './ProcessSteps.vue';

const website = inject<Ref<Website>>('website');
const price = inject<Ref<string>>('price');
const acceptedTerms = ref(false);
const { isTrial } = storeToRefs(useCompanyStore());

const isLegacyTerms = computed(() => website?.value?.termsVersion === 1);

withDefaults(
  defineProps<{
    viewOnly: boolean;
  }>(),
  {
    viewOnly: false,
  },
);

const { mutate: acceptWebsiteTerms, loading } = useMutation(
  ACCEPT_WEBSITE_TERMS,
  () => ({
    variables: {
      input: {},
    },
  }),
);

const emit = defineEmits(['close']);

const acceptTerms = () => {
  acceptWebsiteTerms().then(({ data }) => {
    website.value = {
      ...website.value,
      termsAcceptedAt: data?.acceptWebsiteTerms?.website?.termsAcceptedAt,
      termsVersion: data?.acceptWebsiteTerms?.website?.termsVersion,
    };
    emit('close');
  });
};
</script>
